export function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isNumericLike(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  if (!value.trim()) return false;
  return Number.isFinite(Number(value));
}

function createAliases(prefix, source = {}) {
  const result = {};

  Object.entries(source || {}).forEach(([key, value]) => {
    result[`${prefix}_${key}`] = isNumericLike(value) ? Number(value) : value;
  });

  return result;
}

function normalizeExpressionContext(context = {}) {
  const result = {};

  Object.entries(context || {}).forEach(([key, value]) => {
    if (isNumericLike(value)) result[key] = Number(value);
  });

  return result;
}

export function evaluateExpression(expr, context = {}, fallback = 0) {
  if (expr === null || expr === undefined || expr === "") return fallback;
  if (typeof expr === "number") return expr;

  const text = String(expr).trim();
  if (!text) return fallback;

  if (isNumericLike(text)) return Number(text);

  if (Object.prototype.hasOwnProperty.call(context, text)) {
    const value = context[text];
    return isNumericLike(value) ? Number(value) : value;
  }

  try {
    const safeContext = normalizeExpressionContext(context);
    const keys = Object.keys(safeContext);
    const values = keys.map((k) => safeContext[k]);

    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `"use strict"; return (${text});`);
    const result = fn(...values);

    return isNumericLike(result) ? Number(result) : fallback;
  } catch {
    return fallback;
  }
}

function resolveObjectExpressions(obj = {}, context = {}) {
  const result = {};

  Object.entries(obj || {}).forEach(([key, value]) => {
    result[key] = evaluateExpression(value, { ...context, ...result }, value);
  });

  return result;
}

export function resolveBindings({
  globalParameters = {},
  parentParameters = {},
  definitionDefaults = {},
  parameterBindings = {},
  parameterOverrides = {},
}) {
  const globalAliases = {
    ...createAliases("global", globalParameters),
    ...createAliases("project", globalParameters),
  };

  const parentAliases = createAliases("parent", parentParameters);

  const baseContext = {
    ...(globalParameters || {}),
    ...globalAliases,
    ...(parentParameters || {}),
    ...parentAliases,
  };

  const resolvedDefinitionDefaults = resolveObjectExpressions(
    definitionDefaults,
    baseContext
  );

  const boundContext = {
    ...baseContext,
    ...resolvedDefinitionDefaults,
  };

  const resolvedBindings = resolveObjectExpressions(parameterBindings, boundContext);

  const overrideContext = {
    ...boundContext,
    ...resolvedBindings,
  };

  const resolvedOverrides = resolveObjectExpressions(
    parameterOverrides,
    overrideContext
  );

  return {
    ...boundContext,
    ...resolvedBindings,
    ...resolvedOverrides,
  };
}